import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useInventoryData } from '../../lib/admin-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Package, AlertTriangle, ArrowDownRight, ArrowUpRight, Search } from 'lucide-react';
import { formatRelativeTime, cn } from '../../lib/utils';
export function InventoryPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    inventory,
    alerts,
    movements,
    loading: invLoading
  } = useInventoryData(restaurant?.id);
  const [filter, setFilter] = useState<'all' | 'low' | 'out'>('all');
  const [search, setSearch] = useState('');
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-885">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-886" />
      </div>;
  }
  // Derive alert counts from row-level alerts (real view uses uppercase values)
  const criticalCount = alerts.filter(a => a.alert_level === 'CRITICAL').length;
  const lowCount = alerts.filter(a => a.alert_level === 'LOW').length;
  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.ingredient_name.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (filter === 'out') return item.stock_status === 'out';
    if (filter === 'low') return item.stock_status === 'low' || item.stock_status === 'out';
    return true;
  });
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-887">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-888">
        <div data-id="element-889">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-890">Inventario</h1>
          <p className="text-gray-500" data-id="element-891">Control de stock e ingredientes</p>
        </div>

        {/* Alerts Summary */}
        {(criticalCount > 0 || lowCount > 0) && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-892">
            {criticalCount > 0 && <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-4" data-id="element-893">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0" data-id="element-894">
                  <AlertTriangle className="w-6 h-6 text-red-600" data-id="element-895" />
                </div>
                <div data-id="element-896">
                  <h3 className="font-bold text-red-900" data-id="element-897">Stock Crítico</h3>
                  <p className="text-red-700 text-sm" data-id="element-898">
                    {criticalCount} ingredientes requieren atención inmediata
                  </p>
                </div>
              </div>}
            {lowCount > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4" data-id="element-899">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0" data-id="element-900">
                  <Package className="w-6 h-6 text-amber-600" data-id="element-901" />
                </div>
                <div data-id="element-902">
                  <h3 className="font-bold text-amber-900" data-id="element-903">Stock Bajo</h3>
                  <p className="text-amber-700 text-sm" data-id="element-904">
                    {lowCount} ingredientes por debajo del mínimo sugerido
                  </p>
                </div>
              </div>}
          </div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-905">
          {/* Main Inventory Table */}
          <Card className="lg:col-span-2" data-id="element-906">
            <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4" data-id="element-907">
              <CardTitle data-id="element-908">Ingredientes</CardTitle>

              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-909">
                <div className="relative" data-id="element-910">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-911" />
                  <input type="text" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber w-full" data-id="element-912" />
                </div>
                <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-913">
                  <button onClick={() => setFilter('all')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', filter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')} data-id="element-914">
                    Todos
                  </button>
                  <button onClick={() => setFilter('low')} className={cn('px-3 py-1.5 text-sm font-medium rounded-md transition-colors', filter === 'low' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')} data-id="element-915">
                    Bajos
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-916">
              {invLoading ? <div className="space-y-4" data-id="element-917">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" data-id="element-918" />)}
                </div> : <div className="overflow-x-auto" data-id="element-919">
                  <table className="w-full text-sm text-left" data-id="element-920">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-921">
                      <tr data-id="element-922">
                        <th className="px-4 py-3 rounded-l-lg" data-id="element-923">Ingrediente</th>
                        <th className="px-4 py-3 text-right" data-id="element-924">Stock Actual</th>
                        <th className="px-4 py-3 text-right" data-id="element-925">Mínimo</th>
                        <th className="px-4 py-3 rounded-r-lg" data-id="element-926">Estado</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-927">
                      {filteredInventory.map(item => <tr key={item.inventory_item_id} className="border-b border-gray-50 last:border-0" data-id="element-928">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-929">
                            {item.ingredient_name}
                            {item.supplier && <div className="text-xs text-gray-400 font-normal" data-id="element-930">
                                {item.supplier}
                              </div>}
                          </td>
                          <td className="px-4 py-3 text-right font-bold" data-id="element-931">
                            {item.current_stock}{' '}
                            <span className="text-gray-500 font-normal" data-id="element-932">
                              {item.unit}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500" data-id="element-933">
                            {item.minimum_stock} {item.unit}
                          </td>
                          <td className="px-4 py-3" data-id="element-934">
                            {item.stock_status === 'ok' ? <Badge variant="success" data-id="element-935">OK</Badge> : item.stock_status === 'low' ? <Badge variant="warning" data-id="element-936">Bajo</Badge> : <Badge variant="danger" data-id="element-937">Agotado</Badge>}
                          </td>
                        </tr>)}
                      {filteredInventory.length === 0 && <tr data-id="element-938">
                          <td colSpan={4} className="px-4 py-8 text-center text-gray-500" data-id="element-939">
                            No se encontraron ingredientes
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>}
            </CardContent>
          </Card>

          {/* Recent Movements */}
          <Card data-id="element-940">
            <CardHeader data-id="element-941">
              <CardTitle data-id="element-942">Movimientos Recientes</CardTitle>
            </CardHeader>
            <CardContent data-id="element-943">
              {invLoading ? <Skeleton className="h-64 w-full" data-id="element-944" /> : movements.length > 0 ? <div className="space-y-4" data-id="element-945">
                  {movements.map(mov => {
                // Find item name from inventory view
                const item = inventory.find(i => i.inventory_item_id === mov.inventory_item_id);
                const isDeduction = mov.movement_type === 'sale_consumption' || mov.movement_type === 'waste' || mov.movement_type === 'manual_adjustment';
                const isIncrease = mov.movement_type === 'restock';
                return <div key={mov.id} className="flex items-start gap-3 pb-4 border-b border-gray-50 last:border-0 last:pb-0" data-id="element-946">
                        <div className={cn('w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5', isIncrease ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600')} data-id="element-947">
                          {isIncrease ? <ArrowUpRight className="w-4 h-4" data-id="element-948" /> : <ArrowDownRight className="w-4 h-4" data-id="element-949" />}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-950">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-951">
                            {item?.ingredient_name || 'Item desconocido'}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-952">
                            {mov.movement_type === 'sale_consumption' ? 'Consumo por venta' : mov.movement_type === 'restock' ? 'Reabastecimiento' : mov.movement_type === 'waste' ? 'Desperdicio' : 'Ajuste manual'}
                            {mov.reason && ` • ${mov.reason}`}
                          </p>
                        </div>
                        <div className={cn('text-sm font-bold shrink-0', isIncrease ? 'text-emerald-600' : 'text-red-600')} data-id="element-953">
                          {isIncrease ? '+' : '-'}
                          {Math.abs(mov.quantity)} {item?.unit}
                        </div>
                      </div>;
              })}
                </div> : <p className="text-sm text-gray-500 text-center py-4" data-id="element-954">
                  No hay movimientos recientes
                </p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>;
}